/**
 * Labstep
 *
 * @module grid/SmartTableProtocolValue/coldefs/metadata/options
 * @desc Column Definition for metadata options
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { getFieldsOptions } from 'labstep-web/components/Metadata/Form/fields';
import { ICONS } from 'labstep-web/constants/icons';
import {
  suppressKeyboardEventClearCells,
  suppressKeyboardEventDisableEnterOnEdit,
} from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type {
  CellRendererParams,
  HeaderComponentParamsProps,
} from 'labstep-web/core/DataGrid/types';
import { stopEditingAndFocus } from 'labstep-web/core/DataGrid/utils';
import { FormShowEditSelectOptions } from 'labstep-web/core/Form/ShowEdit/SelectOptions';
import { TextPlaceholder } from 'labstep-web/core/Text/Placeholder';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/types';
import { GridSmartTableColumnHeader } from 'labstep-web/grid/SmartTableProtocolValue/components/ColumnHeader';
import { GridSmartTableProtocolValueService } from 'labstep-web/grid/SmartTableProtocolValue/services/grid-smart-table-protocol-value.service';
import type { Entity } from 'labstep-web/models/entity.model';
import type { Experiment } from 'labstep-web/models/experiment.model';
import { Metadata } from 'labstep-web/models/metadata';
import type { MetadataType } from 'labstep-web/models/metadata/types';
import type { Protocol } from 'labstep-web/models/protocol.model';
import { Resource } from 'labstep-web/models/resource.model';
import { Label } from 'semantic-ui-react';
import styles from './styles.module.scss';

export const getMetadataOptionsColDef = (
  experimentOrProtocol: Experiment | Protocol,
  parentEntityClass: string,
  metadataType: MetadataType,
  metadataLabel: string,
  protocolValueName?: string,
): SmartTableColDef => {
  return {
    colId: protocolValueName
      ? `metadata:${parentEntityClass}:${metadataType}:${metadataLabel}:${protocolValueName}`
      : `metadata:${parentEntityClass}:${metadataType}:${metadataLabel}`,
    sortable: true,
    headerName: metadataLabel,
    headerComponent: GridSmartTableColumnHeader,
    headerComponentParams: (params: HeaderComponentParamsProps) => {
      return {
        icon: protocolValueName
          ? ICONS.metadata.secondary
          : ICONS.metadata.type.options,
        cornerIcon:
          protocolValueName &&
          (parentEntityClass === Resource.entityName
            ? ICONS.resource.primary
            : ICONS.resource_item.primary),
        secondaryHeader:
          protocolValueName &&
          `${protocolValueName}${GridSmartTableProtocolValueService.getMetadataParentPrefix(parentEntityClass)}`,
        toolPanelColumnName: `${GridSmartTableProtocolValueService.getMetadataParentPrefix(parentEntityClass)}${metadataLabel}`,
        experimentOrProtocol,
      };
    },
    editable: (params: EditableCallbackParams) =>
      GridSmartTableProtocolValueService.getMetadataEditable(
        params,
        experimentOrProtocol,
        metadataType,
        metadataLabel,
        parentEntityClass,
        protocolValueName,
      ),
    suppressKeyboardEvent: (params: any): boolean =>
      suppressKeyboardEventDisableEnterOnEdit(params) ||
      suppressKeyboardEventClearCells(params),
    valueClearer: () => null,
    valueGetter: (params: ValueGetterParams) => {
      if (!params.node) {
        return null;
      }

      const metadata =
        GridSmartTableProtocolValueService.getProtocolValueMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        );

      if (!metadata) {
        return null;
      }
      return JSON.stringify(metadata.options);
    },
    valueSetter: (params: ValueSetterParams) => {
      if (!params.node) {
        return false;
      }

      const metadata =
        GridSmartTableProtocolValueService.getProtocolValueMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        );

      if (metadata && params.newValue == null) {
        return GridSmartTableProtocolValueService.updateEntity(
          params,
          metadata.entityName,
          metadata.guid,
          {
            options: null,
          },
        );
      }
      let newValue = params.newValue;
      if (typeof newValue === 'string') {
        try {
          newValue = JSON.parse(newValue);
        } catch {
          return false;
        }
      }

      if (!metadata) {
        return GridSmartTableProtocolValueService.createMetadata(
          params,
          experimentOrProtocol,
          parentEntityClass,
          metadataType,
          metadataLabel,
          protocolValueName,
          {
            options: {
              values: [],
            },
          },
        );
      }

      return GridSmartTableProtocolValueService.updateEntity(
        params,
        metadata.entityName,
        metadata.guid,
        {
          options: newValue,
        },
      );
    },
    cellRenderer: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      const metadata =
        GridSmartTableProtocolValueService.getProtocolValueMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        );

      if (!metadata) {
        const optionsBody: any = {
          options: {
            values: [],
            is_allow_multiple: true,
            is_allow_add: true,
          },
        };

        return (
          <TextPlaceholder
            onClick={() => {
              GridSmartTableProtocolValueService.createMetadata(
                params,
                experimentOrProtocol,
                parentEntityClass,
                metadataType,
                metadataLabel,
                protocolValueName,
                optionsBody,
              );
            }}
          >
            Select
          </TextPlaceholder>
        );
      }

      return (
        <>
          {metadata.optionsSelectedValues.length ? (
            metadata.optionsSelectedValues.map((value: string) => (
              <Label key={value} circular>
                {value}
              </Label>
            ))
          ) : (
            <DataGridPlaceholder
              children="Select"
              params={params}
              editable
            />
          )}
        </>
      );
    },
    cellStyle: (params) =>
      GridSmartTableProtocolValueService.getCellStyle(
        params,
        experimentOrProtocol,
        Metadata.entityName,
        metadataLabel,
        parentEntityClass,
      ),
    cellEditorPopup: true,
    cellEditor: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      const metadata =
        GridSmartTableProtocolValueService.getProtocolValueMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        );

      if (!metadata || String(metadata.id)[0] === '-') {
        stopEditingAndFocus(params);
        return '';
      }

      return (
        <div
          className={styles.container}
          style={{
            width: params.column.getActualWidth(),
          }}
        >
          <FormShowEditSelectOptions
            canEdit={true}
            children={null}
            initialEditingState
            onToggle={(): void => stopEditingAndFocus(params)}
            options={GridSmartTableProtocolValueService.getOptions(
              params,
            )}
            field={
              getFieldsOptions(
                metadata,
                true,
                metadata.is_template,
                true,
              )[0]
            }
            entity={metadata}
            entityName={Metadata.entityName}
          />
        </div>
      );
    },
  };
};
